// Pure helpers for navigating the relationship between a schema's
// `output_structure` hierarchy and its flat `tables` map.
//
// The unified schema browser renders a hierarchy tree on the left and
// the selected level's columns on the right. To do that it needs a
// single, deterministic answer to questions like:
//
//   • Which table corresponds to this level path?
//   • Which tables have no level path at all (orphans / reference data)?
//   • What's the human breadcrumb (`ClinicalData › SubjectData › …`)
//     for a given table?
//   • Does this schema have a meaningful hierarchy to render, or
//     should we degrade to a flat list?
//
// Everything here is a function of `definition` (the schema's
// `definition` dict from the API). No React, no MUI — easy to unit
// test, easy to reuse from both viewer and editor components.

import {
  iterAllLevels,
  type OutputLevel,
  type OutputStructure,
} from './outputStructure';

export interface SchemaDefinitionLike {
  tables?: Record<string, any>;
  output_structure?: OutputStructure | null;
  synthetic_pk_formulas?: Record<string, any>;
}

/** True when there's at least one level in `output_structure`. */
export function hasHierarchy(definition: SchemaDefinitionLike | null | undefined): boolean {
  const struct = definition?.output_structure;
  if (!struct) return false;
  return iterAllLevels(struct).length > 0;
}

export interface SchemaDefinitionWithTemplate extends SchemaDefinitionLike {
  template_summary?: Record<string, unknown> | null;
}

/**
 * Whether the INTERPRET / structural annotation phase can run.
 * Mirrors ``interpret_schema`` in prompt-orchestrator: only template
 * uploads carry ``template_summary``; SQL DDL and flat table uploads do not.
 */
export function supportsStructuralInterpretation(
  definition: SchemaDefinitionWithTemplate | null | undefined,
): boolean {
  return !!definition?.template_summary;
}

/** Snake-cases a CamelCase element name. Backend uses the same shape
 *  to derive table names from element names, so the two converge. */
export function elementNameToTableName(name: string): string {
  if (!name) return name;
  // Insert _ before each uppercase letter that follows a lowercase
  // or digit (ClinicalData -> Clinical_Data) then lowercase.
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .toLowerCase();
}

/**
 * Build a map from a level's path (slash-joined element names from
 * root to leaf) to the table name that level corresponds to.
 *
 * Resolution strategy (in order):
 *   1. If a table's `level_path: List[str]` equals the level path,
 *      use that — backend Issue #566 stamps this explicitly.
 *   2. If a level has a `source_table` field, trust it.
 *   3. Otherwise convert the level's element name to snake_case and
 *      look for an exact match in `tables`.
 *
 * Returns `{}` when there's no hierarchy or no tables.
 */
export function buildLevelToTableMap(
  definition: SchemaDefinitionLike | null | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  const tables = definition?.tables || {};
  const struct = definition?.output_structure;
  if (!struct) return out;

  // Index tables by their persisted level_path for O(1) lookup.
  const tableByLevelPath: Record<string, string> = {};
  Object.entries(tables).forEach(([tableName, tableData]) => {
    const lp: string[] = Array.isArray(tableData?.level_path) ? tableData.level_path : [];
    if (lp.length > 0) {
      tableByLevelPath[lp.join('/')] = tableName;
    }
  });

  const walk = (level: OutputLevel, parentPath: string[]): void => {
    const path = [...parentPath, level.element_name];
    const pathKey = path.join('/');

    // 1. Persisted level_path on a table?
    if (tableByLevelPath[pathKey]) {
      out[pathKey] = tableByLevelPath[pathKey];
    } else if (level.source_table && tables[level.source_table]) {
      // 2. Level's own source_table hint?
      out[pathKey] = level.source_table;
    } else {
      // 3. Snake-case the element name and look it up.
      const candidate = elementNameToTableName(level.element_name);
      if (candidate && tables[candidate]) {
        out[pathKey] = candidate;
      }
    }

    for (const child of level.children || []) {
      walk(child, path);
    }
  };

  for (const lvl of struct.levels || []) walk(lvl, []);
  return out;
}

/** Inverse of `buildLevelToTableMap`: table name → its level path. */
export function buildTableToLevelMap(
  definition: SchemaDefinitionLike | null | undefined,
): Record<string, string[]> {
  const forward = buildLevelToTableMap(definition);
  const out: Record<string, string[]> = {};
  Object.entries(forward).forEach(([pathKey, tableName]) => {
    out[tableName] = pathKey.split('/');
  });
  return out;
}

/**
 * Tables that are not referenced by any level in the hierarchy. These
 * are reference / metadata tables (e.g., `user_ref`) and tables from
 * CSV / SQL schemas that have no hierarchy at all. The unified browser
 * surfaces them under an "Other tables" group at the bottom of the
 * left pane.
 */
export function getOrphanTables(
  definition: SchemaDefinitionLike | null | undefined,
): string[] {
  const tables = definition?.tables || {};
  const used = new Set(Object.values(buildLevelToTableMap(definition)));
  return Object.keys(tables)
    .filter((name) => !used.has(name))
    .sort();
}

/**
 * Human breadcrumb for a table — the slash-separated chain of element
 * names from root to the level that owns this table. Returns `[]` when
 * the table isn't in the hierarchy.
 */
export function breadcrumbFor(
  tableName: string,
  definition: SchemaDefinitionLike | null | undefined,
): string[] {
  return buildTableToLevelMap(definition)[tableName] || [];
}

/**
 * Flatten the hierarchy to a list of `{ pathKey, level, depth }` triples
 * in pre-order. Handy for tree rendering when the consumer wants to
 * keep its own flat state (e.g., search filtering or virtualization).
 */
export interface FlatLevelEntry {
  pathKey: string;
  path: string[];
  level: OutputLevel;
  depth: number;
}

export function flattenLevels(
  structure: OutputStructure | null | undefined,
): FlatLevelEntry[] {
  if (!structure) return [];
  const out: FlatLevelEntry[] = [];
  const walk = (level: OutputLevel, parentPath: string[], depth: number): void => {
    const path = [...parentPath, level.element_name];
    out.push({ pathKey: path.join('/'), path, level, depth });
    for (const child of level.children || []) walk(child, path, depth + 1);
  };
  for (const lvl of structure.levels || []) walk(lvl, [], 0);
  return out;
}

/**
 * Lookup the synthetic-PK formula for a level's key column (if any).
 * Used by the right-pane header to render "ID is built by …".
 */
export interface PkFormulaInfo {
  strategy?: string;
  inputs?: string[];
}

export function pkFormulaForLevel(
  level: OutputLevel | null | undefined,
  definition: SchemaDefinitionLike | null | undefined,
): PkFormulaInfo | null {
  if (!level || !level.key_column) return null;
  const formulas = definition?.synthetic_pk_formulas || {};
  for (const formula of Object.values(formulas)) {
    const f = formula as any;
    if (f?.target_column === level.key_column) {
      return { strategy: f.strategy, inputs: f.inputs };
    }
  }
  return null;
}

/**
 * Rename a column across the schema definition. Updates:
 *   • the column's `name` field in `tables[tableName].columns`
 *   • any FK on the same table that lists `oldName` as a local column
 *   • any FK on any table whose `referenced_columns` lists `oldName`
 *     AND `referenced_table` equals `tableName`
 *   • any `key_column` on a level whose `source_table` is `tableName`
 *   • any `parent_key` on a child level whose parent's `source_table`
 *     is `tableName` (best-effort — exact parent linkage is not always
 *     persisted, so the walk uses `level_path` overlap as a hint)
 *
 * Returns a deep-clone'd definition with the rename applied. Pure
 * function (input not mutated).
 */
export function renameColumn(
  definition: SchemaDefinitionLike,
  tableName: string,
  oldName: string,
  newName: string,
): SchemaDefinitionLike {
  if (!oldName || !newName || oldName === newName) return definition;
  const next: any = JSON.parse(JSON.stringify(definition || {}));
  const tables = next.tables || {};

  const table = tables[tableName];
  if (!table) return next;

  // 1. Rename in this table's columns.
  if (Array.isArray(table.columns)) {
    table.columns = table.columns.map((c: any) =>
      c?.name === oldName ? { ...c, name: newName } : c,
    );
  } else if (table.columns && typeof table.columns === 'object') {
    const nextCols: Record<string, any> = {};
    Object.entries(table.columns).forEach(([k, v]) => {
      nextCols[k === oldName ? newName : k] = v;
    });
    table.columns = nextCols;
  }

  // 2. Update primary_keys / foreign_keys in this table.
  if (Array.isArray(table.primary_keys)) {
    table.primary_keys = table.primary_keys.map((c: string) =>
      c === oldName ? newName : c,
    );
  }
  if (Array.isArray(table.foreign_keys)) {
    table.foreign_keys = table.foreign_keys.map((fk: any) => ({
      ...fk,
      columns: (fk.columns || []).map((c: string) =>
        c === oldName ? newName : c,
      ),
    }));
  }

  // 3. Update foreign_keys on other tables that reference this one.
  Object.entries(tables).forEach(([otherTableName, otherTable]) => {
    if (otherTableName === tableName) return;
    const fks = (otherTable as any)?.foreign_keys;
    if (!Array.isArray(fks)) return;
    (otherTable as any).foreign_keys = fks.map((fk: any) =>
      fk.referenced_table === tableName
        ? {
            ...fk,
            referenced_columns: (fk.referenced_columns || []).map((c: string) =>
              c === oldName ? newName : c,
            ),
          }
        : fk,
    );
  });

  // 4. Update level.key_column and level.parent_key in output_structure.
  const struct = next.output_structure;
  if (struct?.levels) {
    const walk = (level: any): void => {
      if (level.source_table === tableName && level.key_column === oldName) {
        level.key_column = newName;
      }
      for (const child of level.children || []) {
        if (child.parent_key === oldName && level.source_table === tableName) {
          child.parent_key = newName;
        }
        walk(child);
      }
    };
    for (const top of struct.levels) walk(top);
  }

  return next;
}
